import { TemplateGallery } from '@/components/experiences/template-gallery';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useDeleteExperience, useExperiences, type Experience } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/experiences')({ component: ExperiencesPage });

const SURFACE: Record<Experience['type'], string> = {
  modal: 'Modal',
  fullscreen: 'Fullscreen',
  banner: 'Banner',
  bottom_sheet: 'Bottom sheet',
};

function ExperiencesPage(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const { data: experiences, isLoading } = useExperiences(appId);
  const remove = useDeleteExperience(appId);

  const [pendingDelete, setPendingDelete] = useState<Experience | null>(null);
  const [picking, setPicking] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Experiences</h1>
          <p className="text-sm text-muted-foreground">
            The block trees campaigns deliver. Authored in the canvas builder.
          </p>
        </div>
        {experiences?.length ? <Button onClick={() => setPicking(true)}>New experience</Button> : null}
      </div>

      {isLoading ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : !experiences?.length ? (
        // The empty state IS the gallery — the one screen where "pick a starting point" is the
        // whole job, so it does not hide behind a button.
        <div className="space-y-4 rounded-xl border p-6">
          <div>
            <h2 className="font-medium">Start from a template</h2>
            <p className="text-sm text-muted-foreground">
              Every template is a real document: it saves, it publishes, and it renders natively as-is.
            </p>
          </div>
          <TemplateGallery />
        </div>
      ) : (
        <div className="rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Surface</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {experiences.map((experience) => (
                <TableRow key={experience.id}>
                  <TableCell className="font-medium">
                    <Link
                      to="/experiences/$id/edit"
                      params={{ id: experience.id }}
                      className="hover:underline"
                    >
                      {experience.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{SURFACE[experience.type] ?? experience.type}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">v{experience.version}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(experience.updated_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete experience"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setPendingDelete(experience)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={picking} onOpenChange={setPicking}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New experience</DialogTitle>
            <DialogDescription>Pick a starting point. You can change everything afterwards.</DialogDescription>
          </DialogHeader>
          <TemplateGallery />
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingDelete !== null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              An experience a campaign still points at cannot be deleted — the server refuses and tells you
              which campaign. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                const experience = pendingDelete;
                if (!experience) return;
                remove.mutate(experience.id, {
                  onSuccess: () => toast.success('Experience deleted'),
                  // The 409 body carries the reason (a campaign links it). `api` already surfaces the
                  // server's message — showing a generic failure here would hide the one useful fact.
                  onError: (error: Error) => toast.error(error.message),
                });
                setPendingDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
